import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import { createSkinLoader } from '../../shared/skin-loader.js';
import * as defaultSkin from './skins/box.default.js';
import * as wireframeSkin from './skins/box.wireframe.js';

// C+ rendering tier (cplus-rendering-tier-contract-addendum-v1 §10 / RUN-298):
// box is the Glassmorphism C+ hero surface. Lazy-loaded — the skin pulls in
// three + the renderer service, which must not be statically bundled into the
// CSS-skin path. Supersedes the throwaway RUN-380 `webgl-smoke` skin.
const _glassSkin = createSkinLoader('glassmorphism', 'box');

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_PADDING = ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'];
const VALID_DISPLAY = ['block', 'flex', 'inline', 'inline-flex', 'grid', 'inline-grid'];
const DEFAULT_PADDING = 'none';
const DEFAULT_DISPLAY = 'block';

class RunBox extends RunElement {
  #padding = DEFAULT_PADDING;
  #display = DEFAULT_DISPLAY;
  #rendering = false;

  static get observedAttributes() {
    return ['padding', 'display'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'padding': this.padding = newValue; break;
      case 'display': this.display = newValue; break;
    }
  }

  get padding() { return this.#padding; }
  set padding(value) {
    const coerced = VALID_PADDING.includes(String(value).trim())
      ? String(value).trim()
      : DEFAULT_PADDING;
    // Reflect unconditionally: an explicitly-set value that coerces to the
    // current default must still surface on the attribute (the early-return
    // guard previously skipped reflection in that case).
    this.setAttribute('padding', coerced);
    if (this.#padding === coerced) return;
    this.#padding = coerced;
    this.requestUpdate();
  }

  get display() { return this.#display; }
  set display(value) {
    const coerced = VALID_DISPLAY.includes(String(value).trim())
      ? String(value).trim()
      : DEFAULT_DISPLAY;
    this.setAttribute('display', coerced);
    if (this.#display === coerced) return;
    this.#display = coerced;
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'glassmorphism') {
        if (_glassSkin.loaded) {
          this._applySkinTier({
            skinModule: _glassSkin.module,
            styleSheet: _glassSkin.styles,
          });
        } else {
          _glassSkin.load().then(() => this.requestUpdate());
          // CSS fallback while the C+ skin module loads.
          this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
          defaultSkin.render(this);
        }
      } else if (skin === 'wireframe') {
        this._applySkinTier({
          skinModule: wireframeSkin,
          cssApply: () => {
            this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
            wireframeSkin.render(this);
          },
        });
      } else {
        this._applySkinTier({
          skinModule: defaultSkin,
          cssApply: () => {
            this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
            defaultSkin.render(this);
          },
        });
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const instanceSkin = this.getAttribute('skin');
    if (instanceSkin) return instanceSkin;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    // Box is a generic layout container. No ARIA role override needed —
    // the element carries neutral implicit semantics. No ARIA states or
    // properties are architecturally declared for this primitive.
  }
}

if (!customElements.get('run-box')) {
  customElements.define('run-box', RunBox);
}

export { RunBox };
export default RunBox;